/**
 * Android TV Detection and Navigation Utilities
 * Provides utilities for detecting TV devices and managing TV-specific behaviors
 */

export function isTVDevice(): boolean {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent.toLowerCase();
  
  // Exclude desktop browsers first (Mac, Windows, Linux) unless they explicitly indicate TV
  // Desktop browsers should never be detected as TV devices regardless of screen size
  const hasDesktopOS = 
    (userAgent.includes('mac os x') || userAgent.includes('macintosh')) ||
    userAgent.includes('windows') ||
    (userAgent.includes('linux') && !userAgent.includes('android'));
  
  // If it's a desktop OS, only return true if there are explicit TV indicators
  if (hasDesktopOS) {
    const hasTVIndicator = 
      userAgent.includes('tv') ||
      userAgent.includes('smarttv') ||
      userAgent.includes('googletv') ||
      userAgent.includes('androidtv');
    return hasTVIndicator;
  }
  
  // Check for Android TV and other TV platforms
  const isAndroidTV = 
    userAgent.includes('tv') ||
    userAgent.includes('smarttv') ||
    userAgent.includes('googletv') ||
    userAgent.includes('androidtv') ||
    userAgent.includes('bravia') ||
    userAgent.includes('webos') ||
    userAgent.includes('tizen') ||
    // Check for specific Android TV user agents (must explicitly exclude mobile)
    (userAgent.includes('android') && !userAgent.includes('mobile'));

  // Check for screen resolution typical of TVs (1920x1080 or higher)
  const isLargeScreen = window.screen.width >= 1920 && window.screen.height >= 1080;

  // Check if touch is not available (TVs typically don't have touch)
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Only use screen-based detection for non-desktop platforms with TV indicators
  // This handles cases like Android TV boxes that may not have explicit user agent strings
  return isAndroidTV || (isLargeScreen && !isTouchDevice && !hasDesktopOS);
}

export function isTVBrowser(): boolean {
  if (typeof window === 'undefined') return false;

  const userAgent = navigator.userAgent.toLowerCase();
  
  return (
    userAgent.includes('tv') ||
    userAgent.includes('googletv') ||
    userAgent.includes('androidtv') ||
    userAgent.includes('crkey') || // Chromecast
    userAgent.includes('smarttv')
  );
}

/**
 * Get the optimal focus ring style for TV
 */
export function getTVFocusStyle() {
  return {
    outline: '3px solid #E50914',
    outlineOffset: '4px',
    boxShadow: '0 0 0 6px rgba(229, 9, 20, 0.3)',
    transform: 'scale(1.05)',
    transition: 'all 0.2s ease-out'
  };
}

/**
 * Key codes for TV remote controls
 */
export const TV_KEYS = {
  BACK: ['Escape', 'Back', 'XF86Back'],
  OK: ['Enter', 'NumpadEnter'],
  UP: ['ArrowUp'],
  DOWN: ['ArrowDown'],
  LEFT: ['ArrowLeft'],
  RIGHT: ['ArrowRight'],
  PLAY_PAUSE: ['MediaPlayPause', ' ', 'Spacebar'],
  PLAY: ['MediaPlay'],
  PAUSE: ['MediaPause'],
  STOP: ['MediaStop'],
  REWIND: ['MediaRewind', 'r', 'R'],
  FAST_FORWARD: ['MediaFastForward', 'f', 'F'],
  CHANNEL_UP: ['ChannelUp', 'PageUp'],
  CHANNEL_DOWN: ['ChannelDown', 'PageDown'],
  VOLUME_UP: ['VolumeUp'],
  VOLUME_DOWN: ['VolumeDown'],
  MUTE: ['VolumeMute'],
  RED: ['ColorF0Red'],
  GREEN: ['ColorF1Green'],
  YELLOW: ['ColorF2Yellow'],
  BLUE: ['ColorF3Blue'],
} as const;

/**
 * Check if a key event matches any of the specified keys
 */
export function isKeyPressed(event: KeyboardEvent, keys: readonly string[]): boolean {
  return keys.includes(event.key);
}

/**
 * Prevent default scrolling behavior on arrow keys for TV
 */
export function preventDefaultTVKeys(event: KeyboardEvent): void {
  const tvNavigationKeys = [
    ...TV_KEYS.UP,
    ...TV_KEYS.DOWN,
    ...TV_KEYS.LEFT,
    ...TV_KEYS.RIGHT,
  ];
  
  if (tvNavigationKeys.includes(event.key)) {
    event.preventDefault();
  }
}

